import { useEffect, useState } from "react";
import { BellRing } from "lucide-react";
import { api } from "../lib/api";

/** Follow a county and be told when land is filed there. Partners are emailed first. */
export const LandAlerts = ({ county }) => {
  const [meta, setMeta] = useState(null);
  const [counties, setCounties] = useState([]);
  const [form, setForm] = useState({ email: "", role: "County government" });
  const [picked, setPicked] = useState(county ? [county] : []);
  const [state, setState] = useState({ status: "idle" });

  useEffect(() => {
    api.get("/land-alerts/meta").then((r) => setMeta(r.data)).catch(() => setMeta(null));
    api.get("/counties").then((r) => {
      const rows = Array.isArray(r.data) ? r.data : r.data.counties || [];
      setCounties(rows.map((c) => c.county || c.name).filter(Boolean));
    }).catch(() => {});
  }, []);

  useEffect(() => { if (county) setPicked((p) => (p.includes(county) ? p : [county])); }, [county]);

  const submit = async (e) => {
    e.preventDefault();
    setState({ status: "sending" });
    try {
      const r = await api.post("/land-alerts", { ...form, counties: picked });
      setState({ status: "done", data: r.data });
    } catch {
      setState({ status: "error" });
    }
  };

  const label = "mono block text-[0.6rem] uppercase tracking-[0.16em] text-zinc-400";

  return (
    <section className="mt-20 scroll-mt-28" id="land-alerts" data-testid="land-alerts">
      <p className="eyebrow">Follow the land</p>
      <h2 className="display-3 mt-4">Follow How Busy Your County Is</h2>
      <p className="mt-6 max-w-3xl text-sm leading-relaxed text-zinc-400">
        Choose the counties you care about and we email you how many sites were filed there — a count only,
        never an individual site or the person who filed it. Everyone hears the same thing at the same time.
      </p>

      {state.status === "done" ? (
        <div className="mt-8 max-w-3xl border border-ochre/40 bg-[#0b0b0b] p-7" data-testid="land-alerts-done">
          <p className="mono text-[0.6rem] uppercase tracking-[0.16em] text-ochre">Following</p>
          <p className="mt-3 text-sm leading-relaxed text-zinc-300">
            {state.data.counties.length ? state.data.counties.join(", ") : "Every county"}.{" "}
            You will hear how many sites were filed, at the same time as everyone else following the same county.
          </p>
          <p className="mono mt-4 text-[0.6rem] uppercase tracking-[0.16em] text-zinc-500">
            Every email carries a link to stop them.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 grid max-w-4xl gap-5 bg-[#0b0b0b] p-7 md:grid-cols-2"
          data-testid="land-alerts-form">
          <label className="block">
            <span className={label}>Your email</span>
            <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email"
              required data-testid="land-alerts-email"
              className="mt-2 w-full border border-white/20 bg-transparent px-3.5 py-2.5 text-sm text-white focus:border-ochre focus:outline-none" />
          </label>
          <label className="block">
            <span className={label}>You are a</span>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
              data-testid="land-alerts-role"
              className="mt-2 w-full border border-white/20 bg-black px-3.5 py-2.5 text-sm text-white focus:border-ochre focus:outline-none">
              {(meta?.roles || ["Funding partner", "Other"]).map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <label className="block md:col-span-2">
            <span className={label}>Counties to follow — leave empty for all 47</span>
            <select multiple value={picked} data-testid="land-alerts-counties"
              onChange={(e) => setPicked(Array.from(e.target.selectedOptions, (o) => o.value))}
              className="mt-2 h-32 w-full border border-white/20 bg-black px-3.5 py-2.5 text-sm text-white focus:border-ochre focus:outline-none">
              {counties.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="mono flex items-start gap-3 text-[0.6rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-500 md:col-span-2">
            <span>
              No site details are circulated, and no follower is prioritised over another. We do not market
              land, find buyers or vendors, or take any part in a transaction.
            </span>
          </label>
          <div className="md:col-span-2 flex flex-wrap items-center gap-4">
            <button type="submit" disabled={state.status === "sending"} data-testid="land-alerts-submit"
              className="mono inline-flex items-center gap-2 border border-ochre px-6 py-3 text-[0.6rem] uppercase tracking-[0.16em] text-ochre transition-colors hover:bg-ochre hover:text-black disabled:opacity-50">
              <BellRing size={13} /> {state.status === "sending" ? "Setting up…" : "Follow these counties"}
            </button>
            {state.status === "error" ? (
              <span className="mono text-[0.6rem] uppercase tracking-[0.16em] text-maasai" data-testid="land-alerts-error">
                That did not save. Try again in a moment.
              </span>
            ) : null}
          </div>
        </form>
      )}
    </section>
  );
};

export default LandAlerts;
